import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Tabs,
  TabsProps,
  notification,
  Popconfirm,
  Input,
} from "antd";
import { useParams } from "react-router-dom";
import {
  editShortDescriptionBettingWebsite,
  getBettingWebsiteByID,
} from "../action";
import { TbEditCircle } from "react-icons/tb";

type Props = {
  fetchData: () => void;
};

interface FormValues {
  short: {
    en: string;
    ar: string;
    fr: string;
  };
}

const EditShortDescription: React.FC<Props> = ({ fetchData }) => {
  const [form] = Form.useForm<FormValues>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();

  const fetchItem = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const website = await getBettingWebsiteByID(id);
      if (website) {
        form.setFieldsValue({
          short: {
            en: website?.MainInfo?.Description?.Short?.En || "",
            ar: website?.MainInfo?.Description?.Short?.Ar || "",
            fr: website?.MainInfo?.Description?.Short?.Fr || "",
          },
        });
      }
    } catch (err) {
      api.error({
        message: "Failed to fetch website",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);

      const success = await editShortDescriptionBettingWebsite(
        id!,
        values.short
      );

      if (success) {
        fetchData();
        api.success({
          message: "Short description updated successfully",
        });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to update short description",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [id]);

  const languages = ["en", "ar", "fr"] as const;
  const placeholders: Record<(typeof languages)[number], string> = {
    en: "Enter short description",
    ar: "أدخل وصفًا مختصرًا",
    fr: "Entrez une brève description",
  };

  const tabItems: TabsProps["items"] = languages.map((lang) => ({
    key: lang,
    label: lang.toUpperCase(),
    children: (
      <Form.Item
        name={["short", lang]}
        label={`Short Description (${lang.toUpperCase()})`}
      >
        <Input.TextArea
          rows={6}
          className={lang === "ar" ? "text-right" : ""}
          placeholder={placeholders[lang]}
        />
      </Form.Item>
    ),
  }));

  return (
    <Form form={form} layout="vertical">
      {contextHolder}

      <Tabs defaultActiveKey="en" items={tabItems} />

      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to edit the short description?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<TbEditCircle />}
              loading={isLoading}
              type="primary"
              className="w-full"
            >
              {isLoading ? "Saving..." : "Edit Short Description"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditShortDescription;
